import { Bird, Shield, Bug, Check, Phone, AlertTriangle } from "lucide-react";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_e49af65e-59bb-4d0b-86e9-ec5fdd6bec61/artifacts/nf6e3ut7_IMG_1549.png";
const WEBSITE_URL = "pestfreebrisbane.com.au";
const QR_BIRD = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://${WEBSITE_URL}/birds&color=000000&bgcolor=FFFFFF&format=png`;
const QR_PEST = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://${WEBSITE_URL}/pests&color=000000&bgcolor=FFFFFF&format=png`;

export default function DoorHangerPage() {
  return (
    <>
      <><title>Door Hanger Design | Pest Free</title></>
      <div className="min-h-screen bg-slate-200 py-10 print:bg-white print:py-0">
        {/* Print instructions */}
        <div className="max-w-3xl mx-auto px-4 mb-6 print:hidden">
          <div className="bg-white rounded-xl p-5 shadow-lg">
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Door Hanger / Flyer Design</h1>
            <p className="text-slate-600 mb-3">Print double-sided on A5 or DL card stock. Side A = bird proofing, Side B = pest & termite upsell.</p>
            <button onClick={() => window.print()} className="bg-[#7ABC32] text-white font-semibold px-6 py-2 rounded-full hover:bg-[#6AAD28] transition-all">
              Print / Save as PDF
            </button>
          </div>
        </div>

        {/* ==================== SIDE A: BIRD PROOFING ==================== */}
        <div className="max-w-[105mm] mx-auto mb-10 print:mb-0 print:break-after-page" style={{ minHeight: '148mm' }}>
          <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-[#1a1a2e] text-white rounded-2xl print:rounded-none overflow-hidden shadow-2xl" style={{ minHeight: '148mm' }}>
            
            {/* Top offer banner */}
            <div className="bg-amber-500 text-center py-2 px-3">
              <p className="text-sm font-black uppercase tracking-wide text-black">We Found You 10% Off!</p>
            </div>

            {/* Logo */}
            <div className="text-center pt-5 pb-3 px-4">
              <img src={LOGO_WHITE} alt="Pest Free" className="h-16 mx-auto" />
            </div>

            {/* Main headline */}
            <div className="text-center px-5 pb-4">
              <h2 className="text-2xl font-black leading-tight mb-1">
                Pigeons Under Your <span className="text-[#5BC0DE]">Solar Panels?</span>
              </h2>
              <p className="text-sm text-slate-300">They're costing you more than you think.</p>
            </div>

            {/* Warning points */}
            <div className="px-5 space-y-2 pb-4">
              {[
                { icon: AlertTriangle, text: "Bird mites invade your home from nests", color: "#E74C3C" },
                { icon: AlertTriangle, text: "Droppings damage & stain panels", color: "#D2691E" },
                { icon: AlertTriangle, text: "Dry nesting = fire hazard", color: "#E74C3C" },
                { icon: AlertTriangle, text: "Up to 30% solar efficiency loss", color: "#D2691E" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* What we do */}
            <div className="mx-5 bg-[#5BC0DE]/15 border border-[#5BC0DE]/30 rounded-xl p-3 mb-4">
              <p className="text-[#5BC0DE] font-bold text-sm mb-1.5 text-center">We Remove, Clean & Protect</p>
              <div className="space-y-1">
                {[
                  "Remove all nests & debris",
                  "Professional stainless steel solar proofing mesh with epoxy resin coating",
                  "10-year warranty on mesh & install",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-[#7ABC32] flex-shrink-0" />
                    <span className="text-xs">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price teaser */}
            <div className="text-center pb-3 px-5">
              <p className="text-xs text-slate-400 mb-0.5">Bird proofing from</p>
              <div className="flex items-baseline justify-center gap-1.5">
                <span className="text-slate-400 line-through text-lg">$550</span>
                <span className="text-3xl font-black text-[#7ABC32]">$495</span>
              </div>
              <p className="text-amber-400 text-xs font-bold mt-0.5">With your 10% door drop discount!</p>
            </div>

            {/* QR + Phone */}
            <div className="bg-slate-900/80 px-5 py-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 mb-1">Get an instant quote:</p>
                <a href="tel:0737081422" className="flex items-center gap-1.5 text-[#7ABC32] font-bold text-lg">
                  <Phone className="w-4 h-4" /> (07) 3708 1422
                </a>
                <p className="text-[10px] text-slate-500 mt-0.5">{WEBSITE_URL}</p>
              </div>
              <div className="text-center">
                <img src={QR_BIRD} alt="QR Code" className="w-16 h-16 rounded" />
                <p className="text-[8px] text-slate-500 mt-0.5">Scan for quote</p>
              </div>
            </div>
          </div>
        </div>

        {/* ==================== SIDE B: PEST + TERMITE UPSELL ==================== */}
        <div className="max-w-[105mm] mx-auto print:break-after-page" style={{ minHeight: '148mm' }}>
          <div className="bg-white text-slate-800 rounded-2xl print:rounded-none overflow-hidden shadow-2xl border border-slate-200" style={{ minHeight: '148mm' }}>
            
            {/* Top banner */}
            <div className="bg-amber-500 text-center py-2 px-3">
              <p className="text-sm font-black uppercase tracking-wide text-black">Complete Home Protection</p>
            </div>

            {/* Logo */}
            <div className="text-center pt-4 pb-2 px-4">
              <img src={LOGO_DARK} alt="Pest Free" className="h-14 mx-auto mix-blend-darken" />
            </div>

            {/* Pest Control Section */}
            <div className="mx-4 mb-3 bg-[#E74C3C]/5 border border-[#E74C3C]/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-[#E74C3C]" />
                <h3 className="font-black text-base text-[#E74C3C]">Pest Control</h3>
              </div>
              <div className="space-y-1 mb-2">
                {[
                  "Cockroaches, ants, spiders & silverfish",
                  "Internal & external treatment",
                  "Cockroach gel bait application",
                  "Safe for families & pets",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />
                    <span className="text-[11px]">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-baseline justify-between border-t border-[#E74C3C]/10 pt-2">
                <div>
                  <p className="text-[10px] text-slate-500">Standard from</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-slate-400 line-through text-sm">$198</span>
                    <span className="text-xl font-black text-[#E74C3C]">$178</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-500">Comprehensive from</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-slate-400 line-through text-sm">$280</span>
                    <span className="text-xl font-black text-[#E74C3C]">$252</span>
                  </div>
                </div>
              </div>
              <p className="text-amber-600 text-[10px] font-bold text-center mt-1">10% off with this flyer!</p>
            </div>

            {/* Add-ons row */}
            <div className="mx-4 mb-3 grid grid-cols-2 gap-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-center">
                <p className="text-[10px] font-bold text-slate-600 mb-0.5">+ Rodent Treatment</p>
                <p className="text-lg font-black text-slate-800">$85</p>
                <p className="text-[9px] text-slate-500">Bait stations & monitoring</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-center">
                <p className="text-[10px] font-bold text-slate-600 mb-0.5">+ Bird Mite Treatment</p>
                <p className="text-lg font-black text-slate-800">$200</p>
                <p className="text-[9px] text-slate-500">Chemical treatment + follow-up</p>
              </div>
            </div>

            {/* Termite Section */}
            <div className="mx-4 mb-3 bg-[#D2691E]/5 border border-[#D2691E]/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Bug className="w-5 h-5 text-[#D2691E]" />
                <h3 className="font-black text-base text-[#D2691E]">Annual Termite Inspection</h3>
              </div>
              <div className="space-y-1 mb-2">
                {[
                  "Full property inspection with thermal imaging",
                  "Moisture detection & risk assessment",
                  "Detailed report with photos",
                  "Early detection saves thousands in damage",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />
                    <span className="text-[11px]">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-baseline gap-1.5 border-t border-[#D2691E]/10 pt-2">
                <span className="text-[10px] text-slate-500">From</span>
                <span className="text-slate-400 line-through text-sm">$150</span>
                <span className="text-xl font-black text-[#D2691E]">$135</span>
                <span className="text-amber-600 text-[10px] font-bold ml-1">10% off!</span>
              </div>
            </div>

            {/* Bundle callout */}
            <div className="mx-4 mb-3 bg-[#7ABC32]/10 border-2 border-[#7ABC32] rounded-xl p-2.5 text-center">
              <p className="text-xs font-black text-[#7ABC32]">Bundle & Save Even More!</p>
              <p className="text-[10px] text-slate-600">Book pest control + termite inspection together</p>
            </div>

            {/* QR + Phone */}
            <div className="bg-slate-900 px-4 py-3 flex items-center justify-between">
              <div>
                <a href="tel:0737081422" className="flex items-center gap-1.5 text-[#7ABC32] font-bold text-lg">
                  <Phone className="w-4 h-4" /> (07) 3708 1422
                </a>
                <p className="text-[10px] text-slate-400 mt-0.5">QBCC Licensed | {WEBSITE_URL}</p>
              </div>
              <div className="text-center">
                <img src={QR_PEST} alt="QR Code" className="w-14 h-14 rounded" />
                <p className="text-[8px] text-slate-400 mt-0.5">Scan for quote</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
